import { createApp } from 'vue';
import process from 'process';
import { Buffer } from 'buffer';
import * as Sentry from '@sentry/vue';
import { initFeatureFlags } from '@arritech/shared/feature-flags';

import '@/pollyfills';
import App from '@/App.vue';
import { useSettingsStore } from '@/stores/settings';
import { useAuthStore } from '@/stores/auth';
import { ModuleRole } from '@/common/rbac';
import { useCaseDetailsStore } from '@/stores/caseDetails';
import { pinia } from '@/stores/pinia';
import { preferencesStoreKey } from '@arritech/ui/preferences';
import { LoaderSpinner } from '@/common';

import GlobalDirectives from '@/globalDirectives';
import Breadcrumb from '@arritech/ui/Breadcrumb/Breadcrumb.vue';
import initProgress from '@/progressbar';
import config from '@/config';
import { RepositoryFactory } from '@/repositories';

// Plugins
import i18n from '@/i18n';
import { setButtonSplitRouteAvailabilityResolver } from '@arritech/ui/Button';
import { isSplitRouteAvailable } from '@/utils/splitNavigation';

import {
	registerLoginListener,
	requestSharedSession,
} from '@/worker/shareSession';

// Routing
import router, { ignoredRoutes } from '@/routes/router';

// Styling
import 'flag-icons/css/flag-icons.min.css';
import '@arritech/ui/styles/global.scss';
import './assets/sass/nprogress.scss';

initFeatureFlags({ environment: config.featureFlagEnvironment });

if (!window.process) {
	window.process = process;
}

if (!window.Buffer) {
	window.Buffer = Buffer;
}

const app = createApp(App);

setButtonSplitRouteAvailabilityResolver((options) =>
	isSplitRouteAvailable(options)
);

app.use(pinia);
app.use(router);
app.use(i18n);
app.use(GlobalDirectives);

app.component('Breadcrumb', Breadcrumb);
app.component('LoaderSpinner', LoaderSpinner);
app.component('loader-spinner', LoaderSpinner);

Object.defineProperty(app.config.globalProperties, 'authStore', {
	get() {
		return useAuthStore();
	},
});

Object.defineProperty(app.config.globalProperties, 'caseDetailsStore', {
	get() {
		return useCaseDetailsStore();
	},
});

const settingsStore = useSettingsStore(pinia);
const authStore = useAuthStore(pinia);
settingsStore.initialize();

// Shared components remember UI preferences through whatever store the app
// hands them; without one they render the same and remember nothing.
app.provide(preferencesStoreKey, settingsStore);

if (
	authStore.isAuthenticated() &&
	!authStore.needs2FAVerification() &&
	authStore.user_id &&
	// GET /userpreference/:userId requires platform:user (user_preference.go:28);
	// module-only users keep their local/default preferences.
	authStore.can(ModuleRole.PlatformUser)
) {
	RepositoryFactory.get('userPreferences').getPreferencesAndSave(
		authStore.user_id
	);
}

let prevPage = localStorage.getItem('prevPage');
if (
	prevPage &&
	authStore.isAuthenticated() &&
	!ignoredRoutes.includes(prevPage)
) {
	setTimeout(() => {
		prevPage = localStorage.getItem('prevPage');
		router.push(prevPage).then(() => {
			localStorage.removeItem('prevPage');
		});
	}, 300);
}

registerLoginListener(() => {
	if (!ignoredRoutes.includes(window.location.pathname)) {
		localStorage.setItem(
			'prevPage',
			window.location.pathname + window.location.search
		);
	}
	if (!authStore.isAuthenticated()) {
		return;
	}

	setTimeout(() => {
		window.location.reload();
	}, 200);
});

requestSharedSession();

// Sentry
Sentry.init({
	app,
	dsn: config.sentry,
	integrations: [
		Sentry.browserTracingIntegration({ router }),
		Sentry.replayIntegration(),
		Sentry.captureConsoleIntegration(),
		Sentry.httpClientIntegration({
			failedRequestStatusCodes: [[401, 599]],
		}),
		Sentry.httpContextIntegration(),
	],
	sendDefaultPii: true,
	environment: config.environment,
	tracesSampleRate: 1.0,
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 1.0,
});
initProgress(router);

app.mount('#app');
